// Readable numbers from a version's stored analysis (analysis_data), for the Full metering view.
// The analyzer stores levels as linear amplitudes; these are converted to dBFS. Band balance is each
// band group's level relative to the average of the five groups, as the backend's review uses it.

const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
export const toDb = (value) => (num(value) && value > 0 ? 20 * Math.log10(value) : null);

const BAND_GROUPS = [
  ['Low end', ['sub_bass', 'bass']],
  ['Low mids', ['low_mids']],
  ['Mids', ['mids']],
  ['Upper mids', ['high_mids']],
  ['Highs', ['highs', 'presence']],
];

const section = (analysis, key) => (analysis && typeof analysis[key] === 'object' && analysis[key]) || {};

export const readouts = (analysis) => {
  const dynamics = section(analysis, 'dynamics');
  const features = section(analysis, 'features');
  const stereo = section(analysis, 'stereo_field');
  const musical = section(analysis, 'musical_context');
  const bands = section(analysis, 'frequency_distribution');

  const groupDb = BAND_GROUPS.map(([label, names]) => {
    const values = names.map((n) => num(bands[n])).filter((v) => v !== null);
    return [label, values.length ? toDb(values.reduce((a, b) => a + b, 0) / values.length) : null];
  });
  const known = groupDb.map(([, db]) => db).filter((db) => db !== null);
  const average = known.length ? known.reduce((a, b) => a + b, 0) / known.length : null;

  return {
    peakDb: toDb(dynamics.peak_level ?? features.peak_amplitude),
    rmsDb: toDb(dynamics.rms_level),
    crestDb: toDb(dynamics.crest_factor),
    brightnessHz: num(features.spectral_centroid),
    rolloffHz: num(features.spectral_rolloff),
    width: num(stereo.stereo_width),
    correlation: num(stereo.phase_correlation),
    bpm: num(musical.bpm),
    key: typeof musical.estimated_key === 'string' ? musical.estimated_key : null,
    duration: num(analysis?.duration),
    bands: groupDb.map(([label, db]) => ({ label, db: db !== null && average !== null ? db - average : null })),
  };
};

// Statuses for Full metering, each a tone (meter-good / meter-watch / meter-problem) and a word
export const peakStatus = (peakDb) => {
  if (peakDb === null) return null;
  if (peakDb >= -0.1) return { tone: 'problem', word: 'Clipping' };
  if (peakDb > -1) return { tone: 'watch', word: 'Little headroom' };
  return { tone: 'good', word: 'Headroom OK' };
};

export const monoStatus = (correlation) => {
  if (correlation === null) return null;
  if (correlation < 0) return { tone: 'problem', word: 'Cancels in mono' };
  if (correlation < 0.3) return { tone: 'watch', word: 'Check in mono' };
  return { tone: 'good', word: 'Mono safe' };
};

/** "1:05" from seconds */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};
